import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Empty, Input, Modal, Space, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  fetchSupplierOrderConfirmationDrafts,
  rejectSupplierOrderConfirmationDraft,
  type SupplierOrderConfirmationDraft,
} from '../api/supplierOrdersApi';
import { getSupplierOrderErrorMessage } from '../supplierOrderErrorMessages';
import { useFeedback } from '../../../utils/feedback';
import { formatDateTimeEuropean } from '../../../utils/dateUtils';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import ProductNameWithDimensions from '../../../components/products/ProductNameWithDimensions';

const { Text, Paragraph } = Typography;

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 500;

export const formatDraftQuantity = (quantity: number | null, unit: string): string => (
  quantity == null
    ? '—'
    : `${formatLocalizedQuantity(quantity)} ${formatUnit(unit)}`
);

interface SupplierOrderDraftsPanelProps {
  /** Lets the page show the count on its "Szkice do przejrzenia" switch. */
  onCountChange?: (count: number) => void;
}

/**
 * "Szkice do przejrzenia" (decision 2026-10-04 pkt 4; makieta ZD): drafts the
 * system wrote when a customer order was confirmed with missing goods. For
 * ADMIN / OPERATIONS only (the backend gates the list). "Przejrzyj szkic"
 * opens the ZD card in review mode (pick the supplier — the system does not
 * guess — set prices, add goods for stock, send); "Odrzuć szkic" asks for a
 * reason, which the ZK card shows in its history.
 */
const SupplierOrderDraftsPanel: React.FC<SupplierOrderDraftsPanelProps> = ({ onCountChange }) => {
  const { t } = useTranslation(['orders', 'common']);
  const navigate = useNavigate();
  const feedback = useFeedback();
  const [drafts, setDrafts] = useState<SupplierOrderConfirmationDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [rejecting, setRejecting] = useState<SupplierOrderConfirmationDraft | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchSupplierOrderConfirmationDrafts();
      setDrafts(rows);
      setLoadFailed(false);
      onCountChange?.(rows.length);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH && reason.trim().length <= MAX_REASON_LENGTH;

  const submitRejection = async () => {
    if (!rejecting || !reasonValid) return;
    setSubmitting(true);
    try {
      await rejectSupplierOrderConfirmationDraft(rejecting.id, reason.trim());
      feedback.success(t('orders:supplierOrders.drafts.rejectSuccess', {
        orderNumber: rejecting.sourceOrder?.orderNumber || rejecting.orderNumber,
      }));
      setRejecting(null);
      setReason('');
      await load();
    } catch (error) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.drafts.rejectError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && drafts.length === 0) {
    return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  }

  return (
    <div data-testid="supplier-order-drafts-panel">
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {t('orders:supplierOrders.drafts.description')}
      </Paragraph>
      {loadFailed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('orders:supplierOrders.drafts.loadError')}
          action={<Button size="small" onClick={() => void load()}>{t('orders:supplierOrders.drafts.retry')}</Button>}
        />
      )}
      {!loadFailed && drafts.length === 0 && (
        <Empty description={t('orders:supplierOrders.drafts.empty')} />
      )}
      <div style={{ display: 'grid', gap: 12 }}>
        {drafts.map((draft) => (
          <section
            key={draft.id}
            data-testid="supplier-order-draft-card"
            // Usability round 2026-10-05: theme tokens — the white card made
            // the titles unreadable in the dark theme.
            style={{ border: '1px solid var(--app-border)', borderRadius: 10, padding: 16, background: 'var(--app-surface)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <Space wrap size={8}>
                  <Text strong style={{ color: 'var(--app-text)', fontSize: 15 }}>
                    {t('orders:supplierOrders.drafts.cardTitle', {
                      orderNumber: draft.sourceOrder?.orderNumber || t('orders:supplierOrders.drafts.unknownOrder'),
                    })}
                  </Text>
                  <Tag color="blue">{t('orders:supplierOrders.drafts.newTag')}</Tag>
                </Space>
                <div>
                  <Text type="secondary">
                    {[
                      draft.sourceOrder?.customerName,
                      draft.confirmedByName
                        ? t('orders:supplierOrders.drafts.confirmedBy', { name: draft.confirmedByName })
                        : null,
                      draft.createdAt ? formatDateTimeEuropean(draft.createdAt) : null,
                    ].filter(Boolean).join(' · ')}
                  </Text>
                </div>
              </div>
              <Space wrap>
                <Button type="primary" onClick={() => navigate(`/app/supplier-orders/${draft.id}?review=1`)}>
                  {t('orders:supplierOrders.drafts.review')}
                </Button>
                <Button onClick={() => { setRejecting(draft); setReason(''); }}>
                  {t('orders:supplierOrders.drafts.reject')}
                </Button>
              </Space>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) 1fr', gap: '6px 16px', margin: '12px 0 0' }}>
              <dt><Text type="secondary">{t(draft.fulfillment.source === 'FACTORY_DIRECT'
                ? 'orders:supplierOrders.drafts.wholeQuantity'
                : 'orders:supplierOrders.drafts.missingGoods')}</Text></dt>
              <dd style={{ margin: 0 }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {draft.items.map((item) => (
                    <li key={item.id}>
                      <ProductNameWithDimensions
                        name={`${item.name}: ${formatDraftQuantity(item.quantity, item.unit)}`}
                        dimensions={item}
                        dimensionsTestId="supplier-order-draft-item-dimensions"
                      />
                    </li>
                  ))}
                </ul>
              </dd>
              <dt><Text type="secondary">{t('orders:supplierOrders.drafts.fulfillment')}</Text></dt>
              <dd style={{ margin: 0 }} data-testid="supplier-order-draft-fulfillment">
                <Text strong>
                  {t(draft.fulfillment.source === 'FACTORY_DIRECT'
                    ? 'orders:supplierOrders.drafts.fulfillmentFactory'
                    : 'orders:supplierOrders.drafts.fulfillmentWarehouse')}
                </Text>
              </dd>
              {draft.fulfillment.note && (
                <>
                  <dt><Text type="secondary">{t('orders:supplierOrders.drafts.salesMessage')}</Text></dt>
                  <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }} data-testid="supplier-order-draft-message">{draft.fulfillment.note}</dd>
                </>
              )}
              <dt><Text type="secondary">{t('orders:supplierOrders.drafts.supplierAndPrice')}</Text></dt>
              <dd style={{ margin: 0 }}><Text type="secondary">{t('orders:supplierOrders.drafts.supplierAndPriceHint')}</Text></dd>
            </dl>
          </section>
        ))}
      </div>

      <Modal
        open={Boolean(rejecting)}
        title={t('orders:supplierOrders.drafts.rejectTitle', {
          orderNumber: rejecting?.sourceOrder?.orderNumber || rejecting?.orderNumber || '',
        })}
        okText={t('orders:supplierOrders.drafts.rejectConfirm')}
        cancelText={t('common:cancel')}
        okButtonProps={{ danger: true, disabled: !reasonValid, loading: submitting }}
        onOk={() => void submitRejection()}
        onCancel={() => { if (!submitting) setRejecting(null); }}
        destroyOnHidden
      >
        <Paragraph>{t('orders:supplierOrders.drafts.rejectDescription')}</Paragraph>
        <label htmlFor="supplier-order-draft-reject-reason">
          <Text strong>{t('orders:supplierOrders.drafts.rejectReason')}</Text>
        </label>
        <Input.TextArea
          id="supplier-order-draft-reject-reason"
          rows={3}
          maxLength={MAX_REASON_LENGTH}
          showCount
          value={reason}
          placeholder={t('orders:supplierOrders.drafts.rejectReasonPlaceholder')}
          onChange={(event) => setReason(event.target.value)}
          style={{ marginTop: 6 }}
        />
      </Modal>
    </div>
  );
};

export default SupplierOrderDraftsPanel;
